import React from 'react';
import { Grid, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PageHeader from '../../../components/page-header';
import { FilterPanel } from '../../../components/filter-panel';
import { GridPanel, dataGridDefaults } from '../../../components/list-grid-panel';
import { RowAction } from '../../../components/row-action';
import BudgetProgramAppService from '../../../application/platform/sge/budget-program/budget-program-app-service';
import { useArrayList } from '../../../hooks/use-array-list';
import { formatCodeName } from '../components/code-name-label';
import { useBudgetProgramSearchController } from './context/budget-program-search-controller';
import { useBudgetProgramSearchState } from './context/budget-program-search-context';
import { BudgetProgramDrawer } from './components/budget-program-drawer';

export function BudgetProgramSearchComponent() {
    const ctrl = useBudgetProgramSearchController();
    const { parameters, budgetUnits, searchTrigger, drawer } = useBudgetProgramSearchState();
    const { rows, loading } = useArrayList(BudgetProgramAppService.list, parameters, searchTrigger, 'Não foi possível carregar os programas orçamentários');

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 80 },
        { field: 'code', headerName: 'Código', width: 140 },
        { field: 'name', headerName: 'Nome', flex: 1, minWidth: 200 },
        {
            field: 'budgetUnitId', headerName: 'Unidade Orçamentária', width: 240,
            valueGetter: (params) => budgetUnits.find((u) => u.id === params.value)?.name || params.value
        },
        {
            field: 'actions', headerName: 'Ações', width: 100, sortable: false,
            renderCell: (params) => (
                <React.Fragment>
                    <RowAction title="Editar" onClick={() => ctrl.openDrawer(params.row)}>
                        <EditIcon fontSize="small" />
                    </RowAction>
                    <RowAction title="Excluir" color="error" onClick={() => ctrl.handleDelete(params.row)}>
                        <DeleteIcon fontSize="small" />
                    </RowAction>
                </React.Fragment>
            )
        }
    ];

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Programas Orçamentários"
                    subtitle="Gerencie os programas orçamentários cadastrados"
                    breadcrumbs={[{ label: 'Programas Orçamentários' }]}
                />
            </Grid>
            <Grid item xs={12}>
                <FilterPanel onClear={ctrl.handleClearFilters} onSearch={ctrl.handleSearch}>
                    <Grid item xs={12} md={6}>
                        <InputLabel>Buscar por código ou nome</InputLabel>
                        <OutlinedInput fullWidth name="input-search" value={parameters.search || ''} onChange={ctrl.handleOnChange} onKeyDown={(e) => e.key === 'Enter' && ctrl.handleSearch()} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <InputLabel>Unidade Orçamentária</InputLabel>
                        <Select fullWidth name="select-budget-unit" value={parameters.budgetUnitId ?? ''} onChange={ctrl.handleOnChange}>
                            <MenuItem value=""><em>Todas</em></MenuItem>
                            {budgetUnits.map((u) => <MenuItem key={u.id} value={u.id}>{formatCodeName(u)}</MenuItem>)}
                        </Select>
                    </Grid>
                </FilterPanel>
            </Grid>
            <Grid item xs={12}>
                <GridPanel addLabel="Novo programa orçamentário" onAdd={() => ctrl.openDrawer()}>
                    <DataGrid
                        {...dataGridDefaults}
                        rows={rows}
                        columns={columns}
                        loading={loading}
                        initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                    />
                </GridPanel>
            </Grid>

            <BudgetProgramDrawer
                open={drawer.open}
                initial={drawer.record}
                budgetUnits={budgetUnits}
                isSaving={ctrl.isSaving}
                onClose={ctrl.closeDrawer}
                onSave={ctrl.handleSave}
            />
        </React.Fragment>
    );
}
